import { useContainerElementContext } from '@unifyapps/ui/providers/ContainerElementContext';
import { useCallback } from 'react';
import { invariant } from 'ts-invariant';
import { useDrawer } from '../../../../components/Drawer/useDrawer';
import useInterfaceEventBus from '../../../hooks/useInterfaceEventBus';
import { useGlobalAppStateStore } from '../../../stores/app/GlobalAppStateStore';
import type { PageCta } from '../types';

type Params = {
  cta: PageCta;
};

const getDrawerSlotProps = ({ width }: { width: string }) => ({
  root: {
    className: 'isolate h-full absolute',
    style: { width },
  },
  content: {
    className: 'w-full h-full relative bg-primary',
  },
  backdrop: {
    className: 'absolute',
  },
});

export const usePageCtaAction = () => {
  const drawerContext = useDrawer();
  const replaceDrawer = 'replace' in drawerContext ? drawerContext.replace : undefined;
  const hideDrawer = 'hide' in drawerContext ? drawerContext.hide : undefined;
  const isOpen = 'isOpen' in drawerContext ? drawerContext.isOpen : undefined;

  const pageEl = useContainerElementContext();
  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const interfaceRecordId = interfaceRecord?.id;

  const { interfaceEventBus } = useInterfaceEventBus();

  const onClick = useCallback(
    ({ cta }: Params) => {
      if (cta.target === 'drawer' && !isOpen) {
        console.error(
          'Unexpected scenario in PageCtaActionProvider: drawer must be open for cta target drawer to work',
        );
        return;
      }

      if (cta.target === 'drawer' && isOpen) {
        replaceDrawer?.({
          template: 'interfacePage',
          anchor: 'right',
          resizable: true,
          styleBased: Boolean(cta.width),
          slotProps: getDrawerSlotProps({ width: cta.width.custom }),
          container: pageEl,
          header: ' ', // Todo: figure out how to render page header
          componentProps: {
            pageId: cta.page,
            pageInputs: cta.pageInputs,
            interfaceId: interfaceRecordId,
          },
        });
      } else {
        // cta.target === 'page'
        hideDrawer?.();
        invariant(cta.page, 'Unexpected scenario in PageCtaActionProvider: pageId must be present');

        interfaceEventBus.publish('navigateToPageNotificationCta', {
          pageId: cta.page,
          pageInputs: cta.pageInputs,
        });
      }
    },
    [hideDrawer, interfaceRecordId, pageEl, replaceDrawer, isOpen, interfaceEventBus],
  );

  return onClick;
};
